import { Box, Container, Grid, styled } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { BasicButton, ComboSelect, Typography } from '../../../components/atoms';
import { Toast } from '../../../components/molecules/Toast/Toast';
import { ErrorSpan } from '../../../containers/retirement-goals-drawer';
import { useCMSConfig } from './hooks/useCmsConfig';
import { checkFFEnabled } from '../../../utils';
import { FEATURE_NAMES } from '../../../common/constants';

const PropertyTypography = styled(Typography)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 4
}));

const StyledBox = styled(Box)(() => ({
    border: '1px solid #E6E6E6',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.paper',
    width: '100%',
    p: 2,
    margin: '0 0 20px 0'
}));

const StyledInnerGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
}));

const StyledSelect = styled(ComboSelect)(() => ({
    '&.Mui-disabled': {
        cursor: 'not-allowed'
    }
}));

const StyledFormLabelTypography = styled(Typography)(() => ({
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px'
}));

const ActionsRow = styled(Box)(() => ({
    width: '100%',
    borderTop: '1px solid #E6E6E6',
    paddingTop: 18,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12
}));

const ResetButton = styled(BasicButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary[500],
    padding: '20px 30px',
    whiteSpace: 'nowrap',
    color: theme.palette.neutral.white,
    borderRadius: '20px',
    boxShadow: 'none',
    textTransform: 'none'
}));

const SubmitButton = styled(BasicButton)(({ theme }) => ({
    padding: '20px 30px',
    whiteSpace: 'nowrap',
    borderRadius: '20px',
    backgroundColor: theme.palette.primary[500],
    color: theme.palette.neutral.white,
    ':disabled': {
        backgroundColor: theme.palette.neutral[100],
        color: theme.palette.neutral[300],
        cursor: 'not-allowed'
    }
}));

const CurrentContentBox = styled(Box)(() => ({
    padding: 16,
    border: '1px solid #E6E6E6',
    borderRadius: 4,
    background: '#fff'
}));

const StyledTextarea = styled('textarea')(() => ({
    width: '100%',
    padding: 20,
    borderRadius: 12,
    background: '#f5f5f6',
    border: '1px solid #eee',
    minHeight: 140,
    outline: 'none',
    fontSize: 14,
    color: '#333',
    resize: 'none',
    fontFamily: 'TT Commons Pro, Arial, sans-serif',
    '::placeholder': {
        color: '#8a8a8a'
    }
}));

type ConfigType = {
    tenant: string | string[];
    pageUrl: string;
    pageLocator: string;
    language: string;
    newContent: string;
};

const ContentManagement = () => {
    const { t } = useTranslation();
    const tenantOptions: Array<{ value: string; label: string }> = useAppSelector(
        (state) => state?.auth?.user?.admin_tenantList
    );
    const isMultiTenantEnabled = checkFFEnabled(FEATURE_NAMES['TENANT_MULTI_SELECT_CMS_BE_FE_AEX-2805-2825']);
    const isFEComboMultiSelectEnabled = checkFFEnabled(FEATURE_NAMES['TENANT_COMBO_MULTI_SELECT_CMS_FE_AEX-2805']);

    const {
        pages,
        locatorsForPage,
        contentForLocator,
        loading: cmsLoading,
        updateCMSConfigs,
        refresh
    } = useCMSConfig();

    const initialFormValues: ConfigType = {
        tenant: isMultiTenantEnabled ? [] : '',
        pageUrl: '',
        pageLocator: '',
        language: '',
        newContent: ''
    };

    const { control, formState, handleSubmit, reset, setValue, watch } = useForm<ConfigType>({
        mode: 'all',
        reValidateMode: 'onSubmit',
        defaultValues: initialFormValues
    });
    const { errors } = formState;
    const watchedTenant = watch('tenant');
    const watchedPageUrl = watch('pageUrl');
    const watchedPageLocator = watch('pageLocator');
    const watchedLanguage = watch('language');
    const watchedNewContent = watch('newContent');
    const selectedTenant = Array.isArray(watchedTenant) ? watchedTenant[0] || '' : watchedTenant || '';
    const hasSelectedTenant = Array.isArray(watchedTenant) ? watchedTenant.length > 0 : Boolean(watchedTenant);

    const [submitting, setSubmitting] = React.useState(false);
    const [showSuccess, setShowSuccess] = React.useState(false);

    const onSubmit = async (data: ConfigType) => {
        try {
            setSubmitting(true);

            const tenantPayload = isMultiTenantEnabled ? { tenants: data.tenant } : { tenant: data.tenant[0] };

            const payload = {
                ...tenantPayload,
                locator: data.pageLocator,
                content: data.newContent,
                language: data.language
            };

            const resp = await updateCMSConfigs(payload as any);

            if (resp) {
                // On success, clear only new content and show toast
                setValue('newContent', '');
                setShowSuccess(true);
            } else {
                // keep form; error state is handled inside the hook
            }
        } finally {
            setSubmitting(false);
        }
    };

    // When pageUrl changes, clear pageLocator
    useEffect(() => {
        setValue('pageLocator', '');
    }, [watchedPageUrl]);

    // When language changes, clear pageUrl and pageLocator (language is selected after tenant)
    useEffect(() => {
        setValue('pageUrl', '');
        setValue('pageLocator', '');
    }, [watchedLanguage]);

    // Fetch CMS config only when both tenant and language are selected by the user
    useEffect(() => {
        if (isMultiTenantEnabled) {
            if (!hasSelectedTenant || !watchedLanguage) return;
            void refresh({ tenant: selectedTenant, language: watchedLanguage });
        } else {
            if (!watchedTenant || !watchedLanguage) return;
            void refresh({
                tenant: Array.isArray(watchedTenant) ? watchedTenant[0] || '' : watchedTenant,
                language: watchedLanguage
            });
        }
    }, [hasSelectedTenant, watchedLanguage, selectedTenant, watchedTenant, refresh, isMultiTenantEnabled]);

    return (
        <>
            <form id="hook-form" onSubmit={handleSubmit(onSubmit)}>
                <Container>
                    <StyledBox p={3}>
                        <Grid
                            width="100%"
                            item
                            flexDirection="row"
                            container
                            spacing={8}
                            justifyContent="left"
                            alignItems="center"
                            marginBottom={2}
                        >
                            <StyledInnerGrid item xs={12} sm={6} md={6}>
                                <StyledFormLabelTypography>
                                    {isFEComboMultiSelectEnabled ? t('SELECT_TENANTS') : t('SELECT_TENANT')}
                                </StyledFormLabelTypography>
                                <Controller
                                    name="tenant"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            multiple={isFEComboMultiSelectEnabled}
                                            onSelectChange={(value) => {
                                                setValue('pageUrl', '');
                                                setValue('pageLocator', '');
                                                setValue('language', '');
                                                field.onChange(Array.isArray(value) ? value : value ? [value] : []);
                                            }}
                                            selectedValue={Array.isArray(field.value) ? field.value : []}
                                            size="small"
                                            fullWidth
                                            data-testid="tenant-select"
                                            error={!!errors.tenant}
                                            placeholder={
                                                isFEComboMultiSelectEnabled
                                                    ? t('CHOOSE_ONE_OR_MORE_TENANTS')
                                                    : t('CHOOSE_A_TENANT')
                                            }
                                            options={tenantOptions}
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" style={{ fontSize: '12px' }}>
                                        {isFEComboMultiSelectEnabled
                                            ? t('TEXT_SELECT_ONE_OR_MORE_TENANTS_FOR_CONTENT_UPDATES')
                                            : t('TEXT_SELECT_THE_TENANT_FOR_CONTENT_UPDATES')}
                                    </Box>
                                    {errors.tenant && (
                                        <ErrorSpan
                                            ml={2}
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.tenant.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>

                            <StyledInnerGrid item xs={12} sm={6} md={6}>
                                <StyledFormLabelTypography>{t('SELECT_LANGUAGE')}</StyledFormLabelTypography>
                                <Controller
                                    name="language"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            onSelectChange={field.onChange}
                                            selectedValue={field.value}
                                            size="small"
                                            fullWidth
                                            data-testid="language-select"
                                            error={!!errors.language}
                                            disabled={isFEComboMultiSelectEnabled ? !hasSelectedTenant : !watchedTenant}
                                            placeholder={t('CHOOSE_A_LANGUAGE')}
                                            options={[
                                                { label: t('ENGLISH'), value: 'en' },
                                                { label: t('SPANISH'), value: 'es' },
                                                { label: t('GERMAN'), value: 'de' },
                                                { label: t('THAI'), value: 'th' }
                                            ]}
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" style={{ fontSize: '12px' }}>
                                        {t('TEXT_SELECT_THE_SELECT_THE_LANGUAGE_TO_UPDATE')}
                                    </Box>
                                    {errors.language && (
                                        <ErrorSpan
                                            ml={2}
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.language.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>
                        </Grid>

                        <Grid
                            width="100%"
                            item
                            flexDirection="row"
                            container
                            spacing={8}
                            justifyContent="left"
                            alignItems="center"
                            marginBottom={2}
                        >
                            <StyledInnerGrid item xs={12} sm={6} md={6}>
                                <StyledFormLabelTypography>{t('SELECT_PAGE_URL')}</StyledFormLabelTypography>
                                <Controller
                                    name="pageUrl"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            onSelectChange={field.onChange}
                                            selectedValue={field.value}
                                            size="small"
                                            fullWidth
                                            data-testid="page-select"
                                            error={!!errors.pageUrl}
                                            disabled={!watchedLanguage || cmsLoading}
                                            placeholder={t('CHOOSE_A_URL_PAGE')}
                                            options={pages.map((p) => ({ label: t(p.toUpperCase()), value: p }))}
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" style={{ fontSize: '12px' }}>
                                        {t('TEXT_SELECT_THE_SPECIFIC_PAGE_TO_UPDATE')}
                                    </Box>
                                    {errors.pageUrl && (
                                        <ErrorSpan
                                            ml={2}
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.pageUrl.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>

                            <StyledInnerGrid item xs={12} sm={6} md={6}>
                                <StyledFormLabelTypography>{t('SELECT_LOCATOR')}</StyledFormLabelTypography>
                                <Controller
                                    name="pageLocator"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            onSelectChange={field.onChange}
                                            selectedValue={field.value}
                                            size="small"
                                            fullWidth
                                            data-testid="locator-select"
                                            error={!!errors.pageLocator}
                                            disabled={!watchedPageUrl || cmsLoading}
                                            placeholder={t('CHOOSE_A_LOCATOR')}
                                            options={locatorsForPage(watchedPageUrl)}
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" style={{ fontSize: '12px' }}>
                                        {t('TEXT_SELECT_THE_CONTENT_LOCATOR_TO_UPDATE')}
                                    </Box>
                                    {errors.pageLocator && (
                                        <ErrorSpan
                                            ml={2}
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.pageLocator.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>
                        </Grid>

                        {watchedPageLocator && (
                            <>
                                <Grid
                                    width="100%"
                                    item
                                    flexDirection="row"
                                    container
                                    spacing={8}
                                    justifyContent="left"
                                    alignItems="center"
                                    marginBottom={2}
                                >
                                    <StyledInnerGrid item xs={12} sm={12} md={12}>
                                        <StyledFormLabelTypography>{t('CURRENT_CONTENT')}</StyledFormLabelTypography>
                                        <Box style={{ width: '100%' }}>
                                            <CurrentContentBox>
                                                {contentForLocator(watchedPageUrl, watchedPageLocator) ||
                                                    t('NO_CONTENT_AVAILABLE')}
                                            </CurrentContentBox>
                                            <Typography variant="caption">
                                                {t('TEXT_THIS_IS_THE_CURRENT_TEXT_FOR_THE_SELECTED_LOCATOR')}
                                            </Typography>
                                        </Box>
                                    </StyledInnerGrid>
                                </Grid>

                                <Grid
                                    width="100%"
                                    item
                                    flexDirection="row"
                                    container
                                    spacing={8}
                                    justifyContent="left"
                                    alignItems="center"
                                    marginBottom={2}
                                >
                                    <StyledInnerGrid item xs={12} sm={12} md={12}>
                                        <StyledFormLabelTypography>{t('NEW_CONTENT')}</StyledFormLabelTypography>
                                        <Controller
                                            name="newContent"
                                            control={control}
                                            render={({ field }) => (
                                                <StyledTextarea
                                                    {...field}
                                                    placeholder={t('PLACEHOLDER_NEW_CONTENT')}
                                                    rows={6}
                                                />
                                            )}
                                        />
                                        <Typography variant="caption">
                                            {t('TEXT_ENTER_THE_UPDATED_CONTENT_FOR_THIS_LOCATOR')}
                                        </Typography>
                                    </StyledInnerGrid>
                                </Grid>
                            </>
                        )}

                        <ActionsRow>
                            <ResetButton
                                type="button"
                                variant="contained"
                                onClick={() => {
                                    reset(initialFormValues);
                                }}
                            >
                                {t('RESET_FORM')}
                            </ResetButton>

                            <SubmitButton
                                variant="contained"
                                type="submit"
                                disabled={submitting || !watchedNewContent || !watchedNewContent.trim()}
                            >
                                {submitting ? t('SUBMITTING') : t('SUBMIT_CONTENT_UPDATE')}
                            </SubmitButton>
                        </ActionsRow>
                    </StyledBox>
                </Container>
            </form>
            {showSuccess && (
                <Toast
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    heading={t('SUBMIT_CONTENT_UPDATE_SUCCESS')}
                    type="confirm"
                    disappearTime={5}
                    isTimer={true}
                    messages={[t('SUBMIT_CONTENT_UPDATE_SUCCESS')]}
                />
            )}
        </>
    );
};

export default ContentManagement;
